import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook for handling S3 images with auto-refreshing URLs
 * @param {Object} user - The user object containing profileImage
 * @param {Function} setUser - Function to update the user object
 * @returns {Object} - Methods and state for handling S3 images
 */
export const useS3Image = (user, setUser) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Function to refresh a profile image URL
  const refreshProfileImageUrl = useCallback(async () => {
    if (!user?.profileImage?.key || isRefreshing) return null;
    
    try {
      setIsRefreshing(true);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/refresh-profile-image-url`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.profileImage) {
        // Update the user object with the new profile image
        const updatedUser = { ...user, profileImage: response.data.profileImage };
        
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Update state through the provided setter function
        if (setUser) {
          setUser(updatedUser);
        }
        
        return response.data.profileImage.url;
      }
      
      return null;
    } catch (error) {
      console.error('Error refreshing profile image URL:', error);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [user, setUser, isRefreshing]);
  
  // Handler for image load errors
  const handleImageError = useCallback(async (event) => {
    // Prevent infinite loop if refreshing doesn't fix the issue
    event.target.removeAttribute('onerror');
    
    const newUrl = await refreshProfileImageUrl();
    if (newUrl) {
      event.target.src = newUrl;
    }
  }, [refreshProfileImageUrl]);
  
  // Function to get a document preview URL
  const getDocumentPreviewUrl = useCallback(async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents/${documentId}/preview`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.previewUrl) {
        return response.data.previewUrl;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting document preview URL:', error);
      return null;
    }
  }, []);
  
  return {
    refreshProfileImageUrl,
    handleImageError,
    getDocumentPreviewUrl,
    isRefreshing
  };
};

export default useS3Image; 